<script setup lang="ts">
import { AanvragenDomainEntitiesOntvangerDirectePost as OntvangerDirectePost } from '@services/aanvragen-public/orval-api/model/aanvragenDomainEntitiesOntvangerDirectePost';
import { type AanvragenMuterenStartMutatieMutatieDto as StartMutatieDto } from '@services/aanvragen-public/orval-api/model/aanvragenMuterenStartMutatieMutatieDto';
import { SUREbusinessCommonDomainIncassocode as Incassomethode } from '@services/aanvragen-public/orval-api/model/sUREbusinessCommonDomainIncassocode';
import { SUREbusinessCommonDomainPremietermijn as Incassotermijn } from '@services/aanvragen-public/orval-api/model/sUREbusinessCommonDomainPremietermijn';
import { useMuterenStart } from '@services/aanvragen-public/orval-api/muteren-api/muteren-api';
import { VButton } from '@shared/component/VButton';
import VMenuButton from '@shared/component/VMenuButton/VMenuButton.vue';
import VModal from '@shared/component/VModal/VModal.vue';
import VProgressBar from '@shared/component/VProgressBar/VProgressBar.vue';
import VTooltip from '@shared/component/VTooltip/VTooltip.vue';
import { toDateonly } from '@tools/dates';
import { sleep } from '@tools/helpers';
import { useIntervalFn } from '@vueuse/core';
import { computed, ref, toRef } from 'vue';
import { useClaims } from '@auth/useClaims';

import CheckboxField from '@shared/form/CheckboxField/CheckboxField.vue';
import { useErrorHandler } from '@errors/useErrorHandler';
import { usePakketpolisStore } from './pakketpolisStore';
import ExtraOpmerkingenModal from './modals/ExtraOpmerkingenModal.vue';
import RoyementsbevestigingModal, {
  type StartMutatie,
} from './modals/RoyementsbevestigingModal.vue';

export type MutatieGestart = {
  directTeVerwerken: boolean;
  offertenummer: string;
  offerteproductId: number | null | undefined;
  isBeursPakket: boolean;
  wagenparkId?: string | null;
};

const props = defineProps<{
  pakketpolisnummer: string;
}>();

const emit = defineEmits<{
  mutatieGestart: [MutatieGestart];
}>();
const { handleError } = useErrorHandler();
const pakketpolisnummer = toRef(props, 'pakketpolisnummer');
const isMutatiePending = ref(false);
const isExtraOpmerkingenModalOpen = ref<boolean>(false);
const isRoyementsbevestigingModalOpen = ref<boolean>(false);

const claims = useClaims();

const { mutateAsync: startMutatie } = useMuterenStart({
  request: {
    timeout: 2 * 60 * 1000,
  },
});

const store = usePakketpolisStore();
const pakketpolis = computed(() => store.getPakketpolis(pakketpolisnummer.value));
const isBeursPakket = computed(() => pakketpolisnummer.value.startsWith('SBB'));

const aantalMutaties = computed(() => pakketpolis.value?.aantalMutaties ?? 0);
const aantalMutatiesTekst = computed(() => {
  const isPlural = aantalMutaties.value !== 1;
  return `mutatie${isPlural ? 's' : ''} klaargezet`;
});
const latenUitvallen = ref(false);

const progress = ref(0);
const incrementStep = ref(1);
const { pause, resume } = useIntervalFn(
  () => (progress.value += progress.value <= 90 ? incrementStep.value : 0),
  100,
  {
    immediate: false,
  },
);

async function mutatieStarten(dto: StartMutatieDto) {
  isMutatiePending.value = true;
  const aantalPolissen = pakketpolis.value?.originelePakketpolis.subpolissen.length ?? 10;
  incrementStep.value = 5 / aantalPolissen;
  progress.value = 0;
  resume();
  try {
    const res = await startMutatie({ polisnummerpakket: pakketpolisnummer.value, data: dto });
    pause();
    // eslint-disable-next-line no-restricted-syntax
    for await (const i of [92, 94, 96, 98, 100]) {
      progress.value = i;
      await sleep(150);
    }
    isMutatiePending.value = false;
    store.setMutatieKlaargezet(pakketpolisnummer.value);
    const event: MutatieGestart = {
      directTeVerwerken: res.directTeVerwerken,
      offertenummer: res.offertenummer,
      offerteproductId: res.offerteproductId,
      isBeursPakket: dto.isBeursPakket,
      wagenparkId: res.wagenparkId,
    };
    emit('mutatieGestart', event);
  } catch (e: any) {
    pause();
    isMutatiePending.value = false;
    handleError(e);
  }
}

const startknopTekst = computed(() =>
  store.getPakketGeroyeerd(props.pakketpolisnummer) ? 'Royement starten' : 'Mutatie starten',
);

function onUitvallen() {
  latenUitvallen.value = !latenUitvallen.value;
}

const options = computed(() => [
  {
    id: 'uitvallen',
    command: onUitvallen,
    text: 'Mutatie/royement laten uitvallen',
    isSelected: latenUitvallen.value,
  },
]);

const incassomethode = computed(
  () => Incassomethode[pakketpolis.value?.incassomethode as keyof typeof Incassomethode],
);
const incassotermijn = computed(
  () => Incassotermijn[pakketpolis.value?.incassotermijn as keyof typeof Incassotermijn],
);

function openRoyementsbevestigingModal() {
  // check that all mutaties are royementen
  const heeftAlleenRoyementen = store.heeftAlleenRoyeerMutaties(pakketpolisnummer.value);

  if (!heeftAlleenRoyementen) {
    return startDeMutatie();
  }

  isRoyementsbevestigingModalOpen.value = true;
}

function startDeMutatie(event?: StartMutatie | undefined) {
  if (!pakketpolis.value) {
    return;
  }

  return mutatieStarten({
    mutatiedatum: toDateonly(pakketpolis.value.ingangsdatum),
    hoofdvervaldatum: toDateonly(pakketpolis.value.hoofdvervaldatum),
    incassocode: incassomethode.value,
    rekening: pakketpolis.value.rekening,
    termijn: incassotermijn.value,
    forceerUitval: latenUitvallen.value,
    isDummy: false,
    nieuweProducten: pakketpolis.value.nieuweProducten,
    teMuterenPolissen: pakketpolis.value.subpolissen,
    ontvangerDirectePost: event?.ontvangerDirectePost ?? OntvangerDirectePost.Adviseur,
    extraOpmerkingen: pakketpolis.value.extraOpmerkingen,
    isBeursPakket: isBeursPakket.value,
  });
}
</script>

<template>
  <div class="lg:nowrap flex grow flex-wrap items-center gap-4 lg:grow-0">
    <template v-if="pakketpolis && claims.isInternal">
      <VMenuButton
        v-if="pakketpolis.mutatieActies.muteren.isMogelijk"
        menu-position="above"
        :menu-props="{
          model: options,
        }"
        :button-props="{ inverted: true, align: 'center' }"
        class="grow max-lg:w-full lg:grow-0"
      >
        <template #item="{ item }">
          <div class="flex w-[272px]">
            <CheckboxField :path="item.id" :model-value="item.isSelected" :sub-label="item.text" />
          </div>
        </template>
        Interne opties
      </VMenuButton>
    </template>
    <template v-if="pakketpolis">
      <div class="flex grow flex-wrap gap-4 lg:ml-auto lg:grow-0 lg:flex-nowrap">
        <div v-if="aantalMutaties > 0" class="grow font-bold lg:mt-2 lg:grow-0">
          {{ aantalMutaties }} {{ aantalMutatiesTekst }}
        </div>
        <VButton
          v-if="aantalMutaties > 0"
          inverted
          @click="isExtraOpmerkingenModalOpen = true"
          class="grow max-lg:w-full lg:grow-0"
          align="center"
        >
          Extra opmerking
        </VButton>
        <VTooltip
          class="grow lg:grow-0"
          label="Zet eerst een wijziging klaar om de mutatie te starten."
          :active="aantalMutaties === 0"
        >
          <VButton
            :loading="isMutatiePending"
            :disabled="aantalMutaties === 0"
            @click="openRoyementsbevestigingModal"
            align="center"
            class="w-full"
          >
            {{ startknopTekst }}
          </VButton>
        </VTooltip>
      </div>
    </template>
  </div>
  <VModal
    invert-header
    title="Mutatie wordt klaargezet.."
    :is-active="isMutatiePending"
    confirm-button-text=""
    cancel-button-text=""
  >
    <VProgressBar class="mt-3" :progress="progress" />
  </VModal>
  <ExtraOpmerkingenModal
    :pakketpolisnummer="props.pakketpolisnummer"
    v-model:is-open="isExtraOpmerkingenModalOpen"
  />
  <RoyementsbevestigingModal
    :pakketpolisnummer="props.pakketpolisnummer"
    v-model:is-open="isRoyementsbevestigingModalOpen"
    @start-mutatie="startDeMutatie"
  />
</template>
